import { FC } from "react";
import styled from "styled-components";

import { ExportPresetResource } from "@/types/resources";
import { scrollBarStyles } from "@/utils/theme";
import Text from "../Text";
import ActionsDropdown from "../ActionsDropdown";
import { ActionsDropdownButton } from "../ActionsDropdown";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import { ExportGroup, Footer, Screens } from ".";
import Button from "../Button";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { useQueryClient } from "@tanstack/react-query";

const Body = styled.div`
  height: 613px;
  text-align: left;
  overflow-y: auto;
  ${scrollBarStyles};
`;

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  padding: 24px;
`;

const Preset = styled.div`
  display: flex;
  border: 1px solid #e9e9e9;
  border-radius: 4px;
  height: 48px;
  align-items: center;
  padding: 0 12px;
`;

const PresetName = styled(Text)`
  flex: 1;
  margin-right: 12px;
`;

interface Props {
  presets: ExportPresetResource[];
  setCurrentScreen: React.Dispatch<React.SetStateAction<Screens>>;
  exportGroup: ExportGroup;
  onPresetEdit: (preset: ExportPresetResource) => void;
}

const PresetsManagementScreen: FC<Props> = ({
  presets,
  setCurrentScreen,
  exportGroup,
  onPresetEdit,
}) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (preset: ExportPresetResource) => {
    try {
      const confirmed = await alerts.confirm(
        "Are you sure you want to do delete this preset?",
        preset.name
      );

      if (!confirmed) return;

      await api.delete(`/v1/export-presets/${preset.id}`);

      await queryClient.invalidateQueries([
        "export_presets",
        { group: exportGroup },
      ]);

      alerts.success("Preset has been successfully deleted");
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <>
      <Body>
        <List>
          {presets.map((preset) => (
            <Preset key={preset.id}>
              <PresetName $type="smallBold">{preset.name}</PresetName>
              <ActionsDropdown zIndex={50}>
                <ActionsDropdownButton
                  as="button"
                  onClick={() => onPresetEdit(preset)}
                >
                  <span>
                    <PencilIcon />
                  </span>
                  Edit
                </ActionsDropdownButton>
                <ActionsDropdownButton
                  as="button"
                  onClick={() => onDelete(preset)}
                >
                  <span>
                    <TrashIcon />
                  </span>
                  Delete
                </ActionsDropdownButton>
              </ActionsDropdown>
            </Preset>
          ))}
        </List>
      </Body>
      <Footer>
        <Button
          type="button"
          color="secondary"
          onClick={() => setCurrentScreen(Screens.EXPORT)}
        >
          Back
        </Button>
      </Footer>
    </>
  );
};

export default PresetsManagementScreen;
